import { useContext, useEffect, useMemo } from 'react'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import { sortPackageOptions } from 'lib/offer/offerPageUtil'
import { countOccupants, formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formatInlineList } from 'lib/format/formatList'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getAvailablePackagesByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { fetchPackageRecommendations } from 'actions/OfferActions'
import { getRecommendationKey } from 'lib/offer/getRecommendationKey'
import { useIntl } from 'react-intl'

interface Params {
  room?: OfferPageRoom
}

interface SuggestedPackageResult {
  option: App.OfferPackageOptionView
  message: string
}

interface SuggestedPackageFromApiResult {
  result: SuggestedPackageResult | undefined
  isLoading: boolean
}

function serialiseOccupancyByCategory(
  rooms: Array<App.Occupants>,
  maxInfantAge: number,
): string {
  return rooms.map(room => {
    const { adults, children, infants } = countOccupants([room], { maxInfantAge })
    return `${adults}-${children}-${infants}`
  }).join(',')
}

function useSuggestedPackageOptionFromApi({ room }: Params = {}): SuggestedPackageFromApiResult {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const { rooms: allRooms, offer, checkInDate, checkOutDate } = offerPageState

  const maxInfantAge = offer?.property?.maxInfantAge ?? 2

  const packageOptionsAllRooms = useAppSelector(state => getDurationPackageOptions(offerPageState, state))
  const packageOptionsByRoom = useAppSelector(state => getAvailablePackagesByRoom(offerPageState, state))

  const packageOptions = room ? packageOptionsByRoom[room.id] : packageOptionsAllRooms

  const rooms = useMemo(() => room ? [room] : allRooms, [room, allRooms])
  const occupancyString = useMemo(
    () => serialiseOccupancyByCategory(rooms, maxInfantAge),
    [rooms, maxInfantAge],
  )

  const offerId = offer?.id
  const recommendationKey = getRecommendationKey({
    offerId,
    occupancy: occupancyString,
    checkIn: checkInDate,
    checkOut: checkOutDate,
  })

  useEffect(() => {
    if (!offerId) return
    dispatch(fetchPackageRecommendations({
      offerId,
      occupancy: occupancyString,
      checkIn: checkInDate,
      checkOut: checkOutDate,
    }))
  }, [dispatch, offerId, occupancyString, checkInDate, checkOutDate])

  const recommendations = useAppSelector(state => state.offer.packageRecommendations?.[recommendationKey])
  const isLoading = Boolean(
    offerId && (recommendations === undefined || recommendations.fetching),
  )

  return useMemo(() => {
    const sortedPackageOptions = sortPackageOptions(packageOptions)
    const availablePackages = sortedPackageOptions.filter(option => option.capacityValid && option.hotelAvailable)

    if (availablePackages.length <= 1) {
      return {
        result: undefined,
        isLoading,
      } // It would be silly to highlight the most popular option if there was only one option...
    }

    const rankedPackages = recommendations?.data
    if (rankedPackages?.length) {
      const availablePackageIds = new Set(availablePackages.map(p => p.package.id))
      const bestRecommendation = rankedPackages
        .filter(rec => availablePackageIds.has(rec.packageId))[0]

      if (bestRecommendation) {
        const recommendedOption = availablePackages.find(
          p => p.package.id === bestRecommendation.packageId,
        )

        if (recommendedOption) {
          const occupantsMessage = formatInlineList(formatOccupantsShort(rooms).split(', '), 'conjunction', { style: 'long' })

          let message = ''
          if (bestRecommendation.reason === 'BOOKINGS_BY_OCCUPANCY' && occupantsMessage) {
            message = intl.formatMessage(
              {
                id: 'M2QxWVx3G5Xx-fHmKWhh',
                defaultMessage: 'Popular for {occupancy}',
              },
              { occupancy: occupantsMessage },
            )
          } else if (bestRecommendation.reason === 'BOOKINGS_OVERALL') {
            message = intl.formatMessage(
              {
                id: 'IrcNoG-CK1--BRZBTOe7',
                defaultMessage: 'Popular',
              },
            )
          } else {
            message = intl.formatMessage(
              {
                id: 'PgFLEsD215DdFL4P95Id',
                defaultMessage: 'Recommended',
              },
            )
          }

          return {
            result: {
              option: recommendedOption,
              message,
            },
            isLoading,
          }
        }
      }
    }

    return {
      result: undefined,
      isLoading,
    }
  }, [packageOptions, recommendations, rooms, intl, isLoading])
}

export default useSuggestedPackageOptionFromApi
